import { atom, GetRecoilValue, useRecoilValue } from "recoil";
import {
  selectorWithoutCache as selector,
  selectorFamilyWithoutCache as selectorFamily,
} from "util/RecoilWithoutCache";
import { BusinessLineEnum, BusinessSource } from "constants/base";
import {
  MixContractDelegateType,
  MixContractHoldSide,
  MixContractMarginMode,
  MixContractOrderStatus,
  MixContractTimeInForceEnum,
} from "constants/MixContractGlobal";
import { Decimal } from "decimal.js";
import {
  calOpenCost,
  calProfits,
  calRoeRate,
  calValue,
  Zero,
  mergeOpenLevelOrOpenAvgPrice,
  mergeRoe,
} from "helper/formula/MixContractFormula";
import { getSymbolPriceBySymbolId } from "./Ticker";
import {
  ContractPositionLevelList,
  CurrentBusinessLineContractListState,
  getLevel,
  getMixContractInfoBySymbolId,
} from "./Contract";
import {
  getTotalUsdtValue,
  getUserAccountMode,
  MixBalanceModeInfo,
} from "./Account";
import StatisticResult from "util/StatisticResult";
import {
  MixContractFixedPositionInfo,
  MixContractInfo,
  MixContractTokenInfo,
} from "../entity";
import {
  createDefaultTokenTicker,
  getTokenPriceByTokenId,
  MixContractTokenState,
} from "./Token";
import { MixContractBaseFixedPositionListSelector } from "./Position";
import { getContractConfig } from "./PositionComputed";
import { getShareInfoApi } from "api/ct";
import { isSuccess } from "util/Common";
import { formatNumberStringFunc } from "hooks/useDirection";
import { groupBy } from "lodash-es";
import { generateUniKey, inverseGenerateUniKey } from "store/data/Account";
import { isTraderState } from "store/data/User";
// import delegateType2Holdside from 'helper/delegateType2Holdside'

export type MixContractCurrentOrderPushData = {
  averageDealPrice: number; // 平均开仓价，当前委托理论上没有
  businessLine: BusinessLineEnum;
  businessSource: BusinessSource | number;
  businessSourceDesc: string; // -Desc结尾的字段多是用来多语言
  cancelReason: string; // 取消原因-枚举
  cancelReasonDesc: string; // 取消原因-多语言
  clientOid: string; // 暂时不用
  controlType: MixContractTimeInForceEnum; // 时效类型
  controlTypeDesc: string;
  createTime: number; // 创建时间
  dealAmount: string; // 成交量
  dealCount: string; // 成交数量
  delegateCount: string; // 委托数量
  delegatePrice: string; // 委托价格
  delegateType: MixContractDelegateType; // 委托类型 方向 买入开多之类
  delegateTypeDesc: string;
  forcePrice: number; // 爆仓价格 仅爆仓委托
  forceTime: number; // 爆仓时间
  key: string; // 暂时先用该值撤单
  orderNo: string;
  id: string; // 计划委托 止盈止损 返回
  planId: string; // 计划委托 止盈止损 返回
  presetStopLossPrice: string; // 预设止损价
  presetTakeProfitPrice: string; // 预设止盈价
  secondBusinessLine: "N/A";
  status: MixContractOrderStatus; // 委托状态
  statusDesc: string;
  symbolDisplayName: string;
  symbolId: string; // 交易对ID
  tokenDisplayName: string;
  tokenId: string; // 保证金ID
  totalFee: number; // 手续费
  totalProfits: number; // 盈亏
  stopProfitPrice: string; // 预设止盈价格
  stopLossPrice: string; //预设止损价格
  triggerPrice?: string; //触发价格
  triggerType: number; // 触发类型 1 市价 2 合理标记价格
  planType: number; // 计划类型 0 普通计划 1 止盈 2 止损
  updateTime: string; // 更新时间
  remark: string; // 备注
  executeCount: number; // 执行数量
  executePrice: string; // 执行价格
  createTradePrice: number; // 创建时 市场价
  version: number;
  rangeRate: string;
  presetStopLossPriceRatio: string;
  presetTakeProfitPriceRatio: string;
  onlyLow?: boolean; // 是否只减仓
  baseTokenId: string;
  netProfits: string; // 净盈亏
  delegateLeverage: string; // 委托杠杆
  feeCP: string; // 抵扣卷
  feeBonus: string; // 体验卷
  calFee: string; // 全部手续费
  exeStopLossPrice?: string;
  exeTakeProfitPrice?: string;
};

export type MixContractCurrentOrderFixedData = {
  dFreeze: Decimal;
  marginMode: MixContractMarginMode;
  leverage: number;
  delegateLeverage: string; // 委托杠杆
  holdSide?: number; //兼容控件?
} & MixContractCurrentOrderPushData;
export type MixContractIceOrderPushData = {
  id: number; // 策略id
  createTime: number; // 创建时间
  delegateType: MixContractDelegateType; //委托类型 1-开多 2-开空 3-平多 4-平空
};
/**
 * 当前委托
 */
export const MixContractCurrentOrdersState = atom<
  MixContractCurrentOrderPushData[]
>({
  key: "mix-ct-current-orders-state-inner",
  default: [],
});

/**
 * 当前委托 - 计划委托
 */
export const MixContractCurrentPlanOrdersState = atom<
  MixContractCurrentOrderPushData[]
>({
  key: "mix-ct-current-plan-orders-state-inner",
  default: [],
});

/**
 * 当前委托 - 止盈止损委托
 */
export const MixContractCurrentTpslOrdersState = atom<
  MixContractCurrentOrderPushData[]
>({
  key: "mix-ct-current-tpsl-orders-state-inner",
  default: [],
});

/**
 * 当前委托 - 追踪委托委托
 */
export const MixContractCurrentTrackOrdersState = atom<any[]>({
  key: "mix-ct-current-track-orders-state-inner",
  default: [],
});
/**
 * 当前委托 - 冰山委托
 */
export const MixContractCurrentIceOrdersState = atom<any[]>({
  key: "mix-ct-current-ice-orders-state-inner",
  default: [],
});
/**
 * 是否是开仓的委托
 */
export const hasOpenOrders = selector({
  key: "has-open-orders-selector",
  get: ({ get }) => {
    const orders = get(MixContractCurrentOrdersState);
    return (symbolId, tokenId) =>
      orders.some((order) => {
        return (
          (order.delegateType === MixContractDelegateType.OPEN_LONG ||
            order.delegateType === MixContractDelegateType.OPEN_SHORT) &&
          order.symbolId === symbolId &&
          order.tokenId === tokenId
        );
      });
  },
});

/**
 * 抽取订单的杠杆
 * @param order
 * @param mode
 */
export function getOrderLeverage(
  order: MixContractCurrentOrderPushData,
  mode: MixBalanceModeInfo
) {
  // debugger
  let leverage = 0;
  if (mode.marginMode === MixContractMarginMode.FIXED_MARGIN) {
    if (
      order.delegateType === MixContractDelegateType.OPEN_LONG ||
      order?.delegateType === MixContractDelegateType.BUY_IN_SINGLE_SIDE_MODE
    ) {
      leverage = mode.fixedLongLeverage;
    }
    if (
      order?.delegateType === MixContractDelegateType.OPEN_SHORT ||
      order?.delegateType === MixContractDelegateType.SELL_IN_SINGLE_SIDE_MODE
    ) {
      leverage = mode.fixedShortLeverage;
    }
  } else {
    if (
      order?.delegateType === MixContractDelegateType.OPEN_LONG ||
      order?.delegateType === MixContractDelegateType.OPEN_SHORT ||
      order?.delegateType === MixContractDelegateType.BUY_IN_SINGLE_SIDE_MODE ||
      order?.delegateType === MixContractDelegateType.SELL_IN_SINGLE_SIDE_MODE
    ) {
      leverage = mode.crossMarginLeverage;
    }
  }
  return leverage;
}

export function calOrderOpenCost(
  order: MixContractCurrentOrderPushData,
  bp: Decimal.Value,
  leverage: Decimal.Value,
  contractConfig: MixContractInfo
) {
  return calOpenCost(
    order.delegatePrice ?? 0,
    new Decimal(order.delegateCount || 0).sub(order.dealCount || 0),
    bp,
    leverage,
    contractConfig.openCostUpRatio,
    contractConfig.feeRate,
    contractConfig.feeRateUpRatio
  );
}

/***
 * 对当前委托进行数据封装处理
 */

export function formatCurrentOrders(
  get: GetRecoilValue,
  orders: MixContractCurrentOrderPushData[]
): MixContractCurrentOrderFixedData[] {
  return orders.map<MixContractCurrentOrderFixedData>((order) => {
    const { tokenId, symbolId } = order;
    let bp = get(getTokenPriceByTokenId(tokenId)).indexPrice;
    let contractConfig = get(getMixContractInfoBySymbolId(symbolId));
    let mode = get(getUserAccountMode({ tokenId, symbolId }));
    let dFreeze = Zero;
    let leverage = getOrderLeverage(order, mode);

    // 先简单过滤市价的单子
    if (leverage !== 0 && order.delegatePrice) {
      dFreeze = calOrderOpenCost(order, bp, leverage, contractConfig);
    }

    return {
      ...order,
      dFreeze,
      leverage,
      marginMode: mode.marginMode,
      baseSymbol: contractConfig.baseSymbol,
      symbolDisplayName: contractConfig.symbolDisplayName,
    };
  });
}

//逐仓
export const MixContractFixedCurrentOrdersSelector = selector<
  MixContractCurrentOrderFixedData[]
>({
  key: "mix-ct-fixed-current-orders",
  get: ({ get }) => {
    const orders = get(MixContractCurrentOrdersState);
    return formatCurrentOrders(get, orders);
  },
});

export const MixContractFixedCurrentPlanOrdersSelector = selector<
  MixContractCurrentOrderFixedData[]
>({
  key: "mix-ct-fixed-current-plan-orders",
  get: ({ get }) => {
    const orders = get(MixContractCurrentPlanOrdersState);
    return formatCurrentOrders(get, orders);
  },
});

// 所有止盈止损单
export const MixContractFixedCurrentTpslOrdersSelector = selector<
  MixContractCurrentOrderFixedData[]
>({
  key: "mix-ct-fixed-current-tpsl-orders",
  get: ({ get }) => {
    const orders = get(MixContractCurrentTpslOrdersState);
    return formatCurrentOrders(get, orders);
  },
});

//追踪委托
export const MixContractFixedCurrentTrackOrdersSelector = selector<
  MixContractCurrentOrderFixedData[]
>({
  key: "mix-ct-fixed-current-track-orders",
  get: ({ get }) => {
    const orders = get(MixContractCurrentTrackOrdersState);
    return formatCurrentOrders(get, orders);
  },
});
//冰山委托
export const MixContractFixedCurrentIceOrdersSelector = selector<
  MixContractCurrentOrderFixedData[]
>({
  key: "mix-ct-fixed-current-ice-orders",
  get: ({ get }) => {
    const orders = get(MixContractCurrentIceOrdersState);
    return formatCurrentOrders(get, orders);
  },
});
/**
 * 主要就是统计开仓成本
 * modify by rexzhao 20220726
 * add tokenTotalShortOrderMaintain  tokenTotalLongOrderMaintain
 */
export const MixContractCurrentOrderStatisticSelector = selector({
  key: "order-statistic-selector",
  get: ({ get }) => {
    const orderList = get(MixContractFixedCurrentOrdersSelector);
    // 逐仓开仓成本之和(保证金单位)
    let tokenTotalFixedOpenCost = new StatisticResult();
    // 全仓开仓成本之和(保证金单位)
    let tokenTotalCrossedOpenCost = new StatisticResult();
    // 全仓开仓委托维持保证金之和（保证金为单位记）
    let tokenTotalCrossedOpenMaintain = new StatisticResult();
    // /** 逐仓开仓委托维持保证金之和（保证金为单位记） */ //逐仓委托冻结保证金
    // const tokenTotalFixedOpenMaintain = new StatisticResult()
    /** 币对委托维持保证金 */
    const tokenSymbolCrossedOpenMaintain = new Map<string, Decimal>();
    // 逐仓空头委托价值
    let tokenTotalShortOrderMaintain = new StatisticResult();
    // 逐仓多头委托价值
    let tokenTotalLongOrderMaintain = new StatisticResult();
    for (let order of orderList) {
      const {
        leverage,
        marginMode,
        dFreeze,
        tokenId,
        symbolId,
        delegateCount,
        dealCount,
        delegatePrice,
        delegateType,
        onlyLow,
      } = order;
      const bp = get(getTokenPriceByTokenId(tokenId)).indexPrice;
      const count = new Decimal(delegateCount || 0).sub(dealCount || 0);

      const feeRate = get(getMixContractInfoBySymbolId(symbolId)).feeRate;

      // 只统计开仓委托
      if (
        delegateType !== MixContractDelegateType.CLOSE_LONG &&
        delegateType !== MixContractDelegateType.CLOSE_SHORT
      ) {
        if (marginMode === MixContractMarginMode.FIXED_MARGIN) {
          tokenTotalFixedOpenCost.add(tokenId, dFreeze);
          //新增单向开仓委托
          let orderValue = calValue(
            Number(delegateCount) - Number(dealCount),
            delegatePrice ?? 0,
            1
          );
          if (
            delegateType === MixContractDelegateType.BUY_IN_SINGLE_SIDE_MODE
          ) {
            tokenTotalLongOrderMaintain.add(tokenId, orderValue);
          } else {
            tokenTotalShortOrderMaintain.add(tokenId, orderValue);
          }
        } else {
          // 全仓统计开仓部分的维持保证金
          if (!onlyLow) {
            tokenTotalCrossedOpenCost.add(tokenId, dFreeze);
          }
          // const totalUsdtValue = get(getTotalUsdtValue).tokenSymbolHoldUsdtValue.get(
          //     `${tokenId}_${symbolId}`
          // )
          const totalUsdtValue = get(
            getTotalUsdtValue
          ).tokenSymbolHoldUsdtValueForMM.get(`${tokenId}_${symbolId}`);

          const { keepMarginRate } = getLevel(
            get(ContractPositionLevelList(symbolId)),
            totalUsdtValue.toNumber()
          );
          // 委托最低维持保证金 = 数量*开仓价格/保证金汇率*mm => 保证金记
          const maintain = count
            .times(delegatePrice || 0)
            .div(bp)
            .times(new Decimal(keepMarginRate).add(feeRate));

          tokenTotalCrossedOpenMaintain.add(tokenId, maintain);
          let temp = tokenSymbolCrossedOpenMaintain.get(
            `${tokenId}_${symbolId}`
          );
          let tempDecimal = temp ? new Decimal(temp) : Zero;
          tokenSymbolCrossedOpenMaintain.set(
            `${tokenId}_${symbolId}`,
            tempDecimal.add(maintain)
          );
        }
      }
    }

    return {
      tokenTotalFixedOpenCost,
      tokenTotalCrossedOpenCost,
      tokenTotalCrossedOpenMaintain,
      tokenSymbolCrossedOpenMaintain,
      tokenTotalLongOrderMaintain,
      tokenTotalShortOrderMaintain,
    };
  },
});

/**
 *
 */
export type MixContractHistoryOrder = {
  businessLine: number; // 业务线，具体后面会给出枚举
  secondBusinessLine: string;
  createTime: number; // 创建时间
  symbolId: string; // 所属交易对
  tokenId: string; // 所用保证金
  orderNo: number; // 唯一订单号
  delegateType: number; // 详见DelegateType枚举
  orderType: number; // 详见MatchType枚举
  delegateCount: number; // 委托数量
  delegatePrice: number; // 委托价格
  averageDealPrice: number; // 平均成交价，当前委托不存在
  dealCount: number; // 成交数量
  status: number; // 订单状态，同原来的status
  totalProfits: number; // 总实现盈亏，当前委托没有
  totalFee: number; // 总手续费，当前委托没有
  forcePrice: number; // 强平价格
  forceTime: number; // 强平发生时间
  source: number; // 来源
  clientOid: string; // 不明ID，
  cancelReason: string; // 撤单原因，当前委托没有
  controlType: number; // 原TimeInForce
  presetTakeProfitPrice: number; // 预设止盈价
  presetStopLossPrice: number; // 预设止损价
  key: string;
  triggerPrice?: string; //触发价格
  triggerType: number; // 触发类型 1 市价 2 合理标记价格
  planType: number; // 计划类型 0 普通计划 1 止盈 2 止损
  updateTime: string; // 更新时间
  remark: string; // 备注
  executeCount: number; // 执行数量
  executePrice: string; // 执行价格
  createTradePrice: number; // 创建时 市场价
};

// 历史委托 - 普通委托
export const MixContractHistoryOrdersState = atom<MixContractHistoryOrder[]>({
  key: "history-orders-state",
  default: [],
});

// 历史委托 - 计划委托
export const MixContractHistoryPlanOrdersState = atom<
  MixContractHistoryOrder[]
>({
  key: "history-plan-orders-state",
  default: [],
});
// 历史委托 - 止盈止损委托
export const MixContractHistoryTpslOrdersState = atom<
  MixContractHistoryOrder[]
>({
  key: "history-tpsl-orders-state",
  default: [],
});

//历史委托-追踪委托
export const MixContractHistoryTrackingPlans = atom<MixContractHistoryOrder[]>({
  key: "history-track-plans",
  default: [],
});
//历史委托-冰山委托
export const MixContractIceTrackingPlans = atom<MixContractHistoryOrder[]>({
  key: "ice-track-plans",
  default: [],
});
/**
 * 成交明细
 */
export type MixAccountDealInfo = {
  businessLine: number; // 业务线，具体后面会给出枚举
  secondBusinessLine: string;
  createTime: number; // 创建时间
  symbolId: string; // 所属交易对
  tokenId: string; // 所用保证金
  orderNo: number; // 唯一订单号
  dealAmount: number; // 成交额
  dealCount: number; // 成交数量
  dealPrice: number; // 成交价格
  delegateType: number; // 委托类型
  fee: number; // 实际支付 手续费
  calFee: string; // 应付手续费
  feeByCoupon: string; // 抵扣券抵扣手续费
  feeByBonus: string; // 体验金抵扣手续费
  feeTokenId: string; // 手续费结算币种，理论上应该就是tokenID
  forcePrice: number; // 强平价
  forceTime: number; // 强平时间
  liquidity: null; // 不明
  profits: number; // 已实现盈亏
  userId: number; // 用户ID
  key: string;
  onlyLow: boolean; //只减仓
  symbolDisplayName: string; // 交易对名称
  baseTokenId: string;
  netProfits: string; // 净盈亏
  takerMakerFlag?: number; // 流动性方向
};

/**
 * 成交明细
 */
export const MixContractUserAccountDealState = atom<MixAccountDealInfo[]>({
  key: "user-account-deal-state",
  default: [],
});

export type FinancialRecord = {
  balanceChange: number; // 余额变化
  bizNo: string; // 编号
  bizTime: number; // 时间
  bizType: string; // 类型
  bizTypeDesc: string; // 类型描述
  businessLine: number; // 业务线
  createTime: number; // 创建时间
  fee: number; // 手续费
  feeTokenDisplayName: string; // 手续费计价币显示
  feeTokenId: string; // 手续费结算币种ID
  key: string;
  recordNo: number;
  secondBusinessLine: string;
  symbolDisplayName: string; // 交易对名称
  symbolId: string; // 交易对ID
  tokenDisplayName: string; // 保证金币种
  tokenId: string; // 保证金id
  userId: number;
  marginChange: string; // 保证金变化量
  onlyLow?: boolean; // 只减仓
  am?: number; // 保证金模式 1为单币种 2为联合保证金模式
};

export const MixContractFinancialRecordsState = atom<FinancialRecord[]>({
  key: "financial-records-state",
  default: [],
});

/**
 * 当前跟单 当前单带实体类似
 */
export type MixTraceOrderInfo = {
  teacherId: string; // 带单者ID，有这个字段表示为跟单类型
  teacherName: string; // 交易员昵称
  closeDealCount: string; // 平仓张数
  orderNo: string; // 订单号
  productCode: string; // 合约名称
  position: number; // 仓位方向 多1空0
  openLevel: string; // 杠杆倍数
  openDelegateCount: string; // 开仓委托张数
  openDealCount: string; // 开仓成交张数
  openAvgPrice: string; // 开仓均价
  openMarginCount: string; // 累计放入保证金
  closeAvgPrice: string; // 平仓均价
  achievedProfits: string; // 已实现盈亏
  stopProfitPrice: string; // 止盈价格 49%
  stopLossPrice: string; // 止损价格 - 19%
  openTime: number; // 开仓时间
  closeTime: number; // 平仓时间
  openFee: string; // 开仓累计手续费
  closeFee: string; // 平仓累计手续费
  capitalPay: string; // 资金费率支出
  capitalReceive: string; // 资金费率获取
  status: number; // 0初始状态，1开仓完成状态，2平仓完成状态
  symbolId: string;
  tokenId: string;
  delegateType: number;
  createTime: number; // 创建时间
  key: string;
  marginMode: MixContractMarginMode; // 仓位模式
  leverage: string;
  businessLine: number;
  version: number;
};

export const MixContractTraceOrderState = atom<MixTraceOrderInfo[]>({
  key: "trace-order-state",
  default: [],
});
/***
 * 对跟单数据进行格式化处理
 * 计算 未实现盈亏  收益率
 */
export type MixTraceOrderFixedInfo = {
  teacherHeadPic: string; // 交易员头像
  encryptTeacherId: string; // 加密过的交易员id
  teacherId: string; // 带单者ID，有这个字段表示为跟单类型
  teacherName: string; // 交易员昵称
  closeDealCount: string; // 平仓张数
  orderNo: string; // 订单号
  productCode: string; // 合约名称
  position: number; // 仓位方向 多1空0
  openLevel: string; // 杠杆倍数
  openDelegateCount: string; // 开仓委托张数
  openDealCount: string; // 开仓成交张数
  openAvgPrice: string; // 开仓均价
  openMarginCount: string; // 累计放入保证金
  closeAvgPrice: string; // 平仓均价
  achievedProfits: string; // 已实现盈亏
  stopProfitPrice: string; // 止盈价格 49%
  stopLossPrice: string; // 止损价格 - 19%
  openTime: number; // 开仓时间
  closeTime: number; // 平仓时间
  openFee: string; // 开仓累计手续费
  closeFee: string; // 平仓累计手续费
  capitalPay: string; // 资金费率支出
  capitalReceive: string; // 资金费率获取
  status: number; // 0初始状态，1开仓完成状态，2平仓完成状态
  symbolId: string;
  tokenId: string;
  delegateType: number;
  createTime: number; // 创建时间
  key: string;
  marginMode: MixContractMarginMode; // 仓位模式
  leverage: string;
  businessLine: number;
  dUpnl: string; //未实现盈亏
  dUpnlText: string; // 展示的信息
  count: string;
  dRoe: number;
  dRoeText: string;
  positionsOpenAvgPrice?: number;
  positionsOpenMarginCount?: number;
  positionsCount?: number;
  hm?: number;
  positionsOpenAvgPriceText?: string;
};

export const MixTraceOrderSelector = selector<MixTraceOrderFixedInfo[]>({
  key: "mix-trace-order-selector",
  get: ({ get }) => {
    const isTrader = get(isTraderState);
    let positionsArr = [];
    let list = get(MixContractTraceOrderState);
    let positionList = get(MixContractBaseFixedPositionListSelector);
    positionList.forEach((item: any) => {
      let arrlist = list.filter(
        (obj: any) =>
          item.businessLine === obj.businessLine &&
          obj.position === item.holdSide &&
          item.marginMode === obj.marginMode &&
          item.symbolId === obj.symbolId &&
          item.tokenId === obj.tokenId
      );

      arrlist = arrlist.map((obj: any) => {
        return {
          ...obj,
          hm: obj?.hm || item?.holdMode,
          positionsOpenAvgPrice: item.averageOpenPrice,
          positionsOpenMarginCount: item?.margin || 0,
          positionsCount: new Decimal(item.available)
            .add(item.locked)
            .toString(),
        };
      });
      positionsArr = [...positionsArr, ...arrlist];
    });

    list =
      positionList?.length && isTrader
        ? positionsArr.sort((a, b) => b.openTime - a.openTime)
        : list;

    return list.map<MixTraceOrderFixedInfo>((item: MixTraceOrderFixedInfo) => {
      const { symbolId, tokenId } = item;
      let ticker = get(getSymbolPriceBySymbolId(symbolId));
      let contractInfo = get(getMixContractInfoBySymbolId(symbolId));
      // 保证金币种的汇价
      const tokenMarkPrice = get(getTokenPriceByTokenId(tokenId)).indexPrice;
      // 持仓数量
      let count = new Decimal(item.openDealCount)
        .minus(item.closeDealCount)
        .toString();
      let dUpnl = calProfits(
        item?.positionsOpenAvgPrice
          ? item.positionsOpenAvgPrice
          : item.openAvgPrice,
        ticker.price,
        count,
        tokenMarkPrice,
        item.position === 1 ? 1 : 2
      );

      let dUpnlText = "";
      if (dUpnl.isPositive()) {
        dUpnlText += "+";
      }
      dUpnlText += formatNumberStringFunc(
        dUpnl.toFixed(
          contractInfo.placeConfig.propertyHighPlace,
          Decimal.ROUND_FLOOR
        )
      );

      let dRoe = calRoeRate(
        dUpnl,
        item?.positionsOpenMarginCount
          ? item.positionsOpenMarginCount
          : item.openMarginCount,
        item?.positionsOpenMarginCount ? 1 : item.marginMode,
        item?.positionsCount ? item?.positionsCount : count,
        item?.positionsOpenAvgPrice
          ? item.positionsOpenAvgPrice
          : item.openAvgPrice,
        tokenMarkPrice,
        item.openLevel
      );
      let dRoeText = "";
      if (dRoe > 0) {
        dRoeText += "+";
      }
      dRoeText += formatNumberStringFunc(dRoe, "%");
      return {
        ...item,
        dUpnl: dUpnl.toString(),
        count,
        dUpnlText,
        dRoe,
        dRoeText,
        positionsOpenAvgPriceText: item?.positionsOpenAvgPrice
          ? new Decimal(item.positionsOpenAvgPrice).toFixed(
              contractInfo.placeConfig.pricePlace,
              item.position === MixContractHoldSide.LONG_POSITIONS
                ? Decimal.ROUND_UP
                : Decimal.ROUND_DOWN
            )
          : 0,
      };
    });
  },
});

/***
 * 合约跟单带单数据汇总 参考主站逻辑
 */
export const MixCopyTradingSummarySelector = selector<MixTraceOrderFixedInfo[]>(
  {
    key: "mix-trace-order-summary-selector",
    get: ({ get }) => {
      let copytradingOrderList = get(MixTraceOrderSelector);

      // list转map 合约跟/带单列表归类
      const getSummaryMap = copytradingOrderList.reduce((obj, item) => {
        const key = item.symbolId + "_" + item.tokenId + "_" + item.marginMode;
        if (obj[key]) {
          obj[key].push(item);
        } else {
          obj[key] = [item];
        }
        return obj;
      }, {});

      // 数组数据汇总 得到map
      const summaryCalMix = (orders) => {
        let orderItem = orders[0];
        const { symbolId, tokenId } = orders[0];
        const ticker = get(getSymbolPriceBySymbolId(symbolId));
        // 保证金币种的汇价
        const tokenMarkPrice = get(getTokenPriceByTokenId(tokenId)).indexPrice;
        const contractInfo = get(getMixContractInfoBySymbolId(symbolId));

        let leverageArr = []; // 合成杠杠
        let countArr = []; // 合成持仓数量
        let marginArr = []; // 合成保证金
        let openAvgPriceArr = []; // 合成开仓均价
        let dUpnl = new Decimal(0); // 未实现盈亏

        orders.forEach((item) => {
          // 杠杠
          leverageArr.push(Number(item.openLevel));
          // 持仓
          countArr.push(Number(item.count));
          // 开仓均价
          openAvgPriceArr.push(Number(item.openAvgPrice));
          // 保证金
          marginArr.push(Number(item.openMarginCount));

          //未实现盈亏
          let profits = calProfits(
            item.openAvgPrice,
            ticker.price,
            item.count,
            tokenMarkPrice,
            item.position
          );
          dUpnl = dUpnl.add(profits || 0);
        });

        // 合成杠杆
        const mergeLevel = mergeOpenLevelOrOpenAvgPrice(
          leverageArr,
          marginArr,
          0
        );
        // 合成持仓
        const count = countArr
          .reduce((prev, current) => {
            return prev.plus(new Decimal(current));
          }, new Decimal(0))
          .toString();
        // 合成开仓均价
        const mergeopenAvgPrice = mergeOpenLevelOrOpenAvgPrice(
          openAvgPriceArr,
          countArr,
          1
        );
        // 合成保证金
        const openMarginCount = marginArr
          .reduce((prev, current) => {
            return prev.plus(new Decimal(current));
          }, new Decimal(0))
          .toString();

        // 未实现盈亏
        let dUpnlText = "";
        if (dUpnl.isPositive()) {
          dUpnlText += "+";
        }
        dUpnlText += dUpnl.toFixed(
          contractInfo.placeConfig.propertyHighPlace,
          Decimal.ROUND_FLOOR
        );

        // 合成收益率
        let dRoe = mergeRoe(
          orderItem.position,
          ticker.price,
          mergeopenAvgPrice,
          mergeLevel,
          contractInfo.placeConfig.propertyHighPlace
        );
        let dRoeText = "";
        if (dRoe > 0) {
          dRoeText += "+";
        }
        dRoeText += formatNumberStringFunc(dRoe, "%");

        return {
          ...orderItem,
          openLevel: mergeLevel,
          count,
          openAvgPrice: mergeopenAvgPrice,
          openMarginCount: openMarginCount,
          dUpnl: dUpnl.toString(),
          dUpnlText,
          dRoe,
          dRoeText,
        };
      };

      // 处理map每个key的数组
      let newlist = [];
      Object.values(getSummaryMap).forEach((arr) => {
        //多头数据
        let longDataArr = arr.filter((item) => item.position === 1);
        //空头数据
        let shortDataArr = arr.filter((item) => item.position === 2);
        if (longDataArr.length) {
          newlist.push(summaryCalMix(longDataArr));
        }
        if (shortDataArr.length) {
          newlist.push(summaryCalMix(shortDataArr));
        }
      });
      return newlist;
    },
  }
);

/**
 * 获取保证金ticker的工具函数
 * @param tokenTickers
 * @param tokenId
 * @param quoteTokenId
 */
export function getTokenTicker(
  tokenTickers: MixContractTokenInfo[],
  tokenId,
  quoteTokenId
) {
  return (
    tokenTickers.find(
      (t) => t.baseTokenId === tokenId && t.quoteTokenId === quoteTokenId
    ) || createDefaultTokenTicker()
  );
}

export const MixContractCurrentOrdersByUnionKeySelector = selector({
  key: "mix-ct-current-orders-by-union-key-selector",
  // TODO: 确认下是否可以用 symbolId
  get: ({ get }) =>
    groupBy(
      get(MixContractCurrentOrdersState),
      ({ symbolId, tokenId }) => `${tokenId}_${symbolId}`
    ),
});

/**
 * 获取币对总委托价值
 */
export const MixContractTotalOrderValueByUnionKey = selectorFamily<
  Decimal,
  string
>({
  key: "mix-ct-total-order-value-by-union-key-selector-family",
  get:
    (unionKey) =>
    ({ get }) => {
      const orders =
        get(MixContractCurrentOrdersByUnionKeySelector)[unionKey] || [];
      let sum = Decimal.sum(
        ...orders.map(
          ({ delegateCount, delegatePrice, dealCount, delegateType }) => {
            // 委托价值
            // 注意：只算未成交的部分 只计算开仓
            if (
              delegateType === MixContractDelegateType.CLOSE_LONG ||
              delegateType === MixContractDelegateType.CLOSE_SHORT
            ) {
              return 0;
            }
            return calValue(
              Number(delegateCount) - Number(dealCount),
              delegatePrice ?? 0,
              1
            );
          }
        ),
        0
      );
      return sum;
    },
});

// //逐仓委托冻结保证金
// export const FixedOrderMaintainValueCollector = selector({
//     key: 'fixed-order-maintain-value-collector',
//     get: ({ get }) => {
//         const contractList = get(CurrentBusinessLineContractListState)
//         const tokenTickers = get(MixContractTokenState)
//         return (
//             orders: MixContractCurrentOrderFixedData[],
//             positionList: MixContractFixedPositionInfo[]
//         ) => {
//             const tokenSymbolSideFixedLongUsdtValue = new StatisticResult()
//             const tokenSymbolSideFixedShortUsdtValue = new StatisticResult()
//             const tokenTotalFixedOrderMaintainValue = new StatisticResult()
//             orders
//                 .filter(
//                     o =>
//                         o.marginMode === MixContractMarginMode.FIXED_MARGIN &&
//                         o.delegatePrice &&
//                         (o.delegateType === MixContractDelegateType.OPEN_LONG ||
//                             o.delegateType === MixContractDelegateType.OPEN_SHORT)
//                 )
//                 .forEach(o => {
//                     const {
//                         symbolId,
//                         tokenId,
//                         delegateType,
//                         delegateCount,
//                         delegatePrice,
//                         dealCount
//                     } = o
//                     // 注意：只算未成交的部分
//                     let orderValue = calValue(
//                         Number(delegateCount) - Number(dealCount),
//                         delegatePrice ?? 0,
//                         1
//                     )
//                     // let holdSide = delegateType2Holdside(delegateType)
//                     const unionKey = generateUniKey(tokenId, symbolId)
//                     let isLong = delegateType === MixContractDelegateType.OPEN_LONG
//                     let isShort = delegateType === MixContractDelegateType.OPEN_SHORT
//                     if (isLong) {
//                         tokenSymbolSideFixedLongUsdtValue.add(unionKey, orderValue)
//                     }
//                     if (isShort) {
//                         tokenSymbolSideFixedShortUsdtValue.add(unionKey, orderValue)
//                     }
//                     // let positionValue = Zero
//                     // let isLong = delegateType === MixContractDelegateType.OPEN_LONG
//                     // let isShort = delegateType === MixContractDelegateType.OPEN_SHORT
//                     // // 尝试找到对应的仓位
//                     // const positions = positionList.filter(p => {
//                     //     if (p.symbolId === symbolId && p.tokenId === tokenId) {
//                     //         return (
//                     //             (isLong && p.holdSide === MixContractHoldSide.LONG_POSITIONS) ||
//                     //             (isShort && p.holdSide === MixContractHoldSide.SHORT_POSITIONS)
//                     //         )
//                     //     } else {
//                     //         return false
//                     //     }
//                     // })
//                     // //单方向的  委托是多 这里就是多 委托是空这里就是空
//                     // positionValue = position.dPricedValue

//                     // const feeRate = contractConfig.feeRate

//                     // const contractLevelList = get(
//                     //     ContractPositionLevelList(contractConfig.symbolId)
//                     // )
//                     // // 查询仓位档位
//                     // const levelInfo = getLevel(
//                     //     contractLevelList,
//                     //     orderValue.add(positionValue).toNumber()
//                     // )
//                     // let mm = levelInfo.keepMarginRate
//                     // let finalValue = orderValue
//                     //     .div(tokenMarkPrice)
//                     //     .times(new Decimal(mm).add(feeRate))
//                     // tokenTotalFixedOrderMaintainValue.add(tokenId, finalValue)
//                 })
//             tokenSymbolSideFixedLongUsdtValue.forEach((orderValue, uniKey) => {
//                 // 尝试找到对应的仓位
//                 const position = positionList.find(p => {
//                     if (generateUniKey(p.tokenId, p.symbolId) === uniKey) {
//                         return p.holdSide === MixContractHoldSide.LONG_POSITIONS
//                     } else {
//                         return false
//                     }
//                 })
//                 const { tokenId, symbolId } = inverseGenerateUniKey(uniKey)
//                 const contractConfig = getContractConfig(contractList, symbolId)
//                 // 保证金币种的汇价
//                 const tokenMarkPrice = get(getTokenPriceByTokenId(tokenId)).indexPrice
//                 let positionValue = Zero
//                 if (position) {
//                     //单方向的  委托是多 这里就是多 委托是空这里就是空
//                     positionValue = position.dPricedValueForMM
//                 }

//                 const feeRate = contractConfig.feeRate

//                 const contractLevelList = get(ContractPositionLevelList(contractConfig.symbolId))
//                 // 查询仓位档位
//                 const levelInfo = getLevel(
//                     contractLevelList,
//                     orderValue.add(positionValue).toNumber()
//                 )
//                 let mm = levelInfo.keepMarginRate
//                 let finalValue = orderValue.div(tokenMarkPrice).times(new Decimal(mm).add(feeRate))
//                 tokenTotalFixedOrderMaintainValue.add(tokenId, finalValue)
//             })
//             tokenSymbolSideFixedShortUsdtValue.forEach((orderValue, uniKey) => {
//                 // 尝试找到对应的仓位
//                 const position = positionList.find(p => {
//                     if (generateUniKey(p.tokenId, p.symbolId) === uniKey) {
//                         return p.holdSide === MixContractHoldSide.SHORT_POSITIONS
//                     } else {
//                         return false
//                     }
//                 })
//                 const { tokenId, symbolId } = inverseGenerateUniKey(uniKey)
//                 const contractConfig = getContractConfig(contractList, symbolId)
//                 // 保证金币种的汇价
//                 const tokenMarkPrice = get(getTokenPriceByTokenId(tokenId)).indexPrice
//                 let positionValue = Zero
//                 if (position) {
//                     //单方向的  委托是多 这里就是多 委托是空这里就是空
//                     positionValue = position.dPricedValueForMM
//                 }

//                 const feeRate = contractConfig.feeRate

//                 const contractLevelList = get(ContractPositionLevelList(contractConfig.symbolId))
//                 // 查询仓位档位
//                 const levelInfo = getLevel(
//                     contractLevelList,
//                     orderValue.add(positionValue).toNumber()
//                 )
//                 let mm = levelInfo.keepMarginRate
//                 let finalValue = orderValue.div(tokenMarkPrice).times(new Decimal(mm).add(feeRate))
//                 tokenTotalFixedOrderMaintainValue.add(tokenId, finalValue)
//             })
//             return tokenTotalFixedOrderMaintainValue
//         }
//     }
// })

//逐仓委托冻结保证金 重写一个
export const FixedOrderMaintainValueStateSelector = selector({
  key: "fixed-order-maintain-value-state-Selector",
  get: ({ get }) => {
    const contractList = get(CurrentBusinessLineContractListState);
    const orders = get(MixContractFixedCurrentOrdersSelector);
    const positionList = get(MixContractBaseFixedPositionListSelector);
    const tokenSymbolSideFixedLongUsdtValue = new StatisticResult();
    const tokenSymbolSideFixedShortUsdtValue = new StatisticResult();
    const tokenTotalFixedOrderMaintainValue = new StatisticResult();
    orders
      .filter(
        (o) =>
          o.marginMode === MixContractMarginMode.FIXED_MARGIN &&
          o.delegatePrice &&
          (o.delegateType === MixContractDelegateType.OPEN_LONG ||
            o.delegateType === MixContractDelegateType.OPEN_SHORT)
      )
      .forEach((o) => {
        const {
          symbolId,
          tokenId,
          delegateType,
          delegateCount,
          delegatePrice,
          dealCount,
        } = o;

        // const contractConfig = getContractConfig(contractList, symbolId)
        // 保证金币种的汇价
        // const tokenMarkPrice = get(getTokenPriceByTokenId(tokenId)).indexPrice
        // 委托价值
        // 注意：只算未成交的部分
        let orderValue = calValue(
          Number(delegateCount) - Number(dealCount),
          delegatePrice ?? 0,
          1
        );
        // let holdSide = delegateType2Holdside(delegateType)
        const unionKey = generateUniKey(tokenId, symbolId);
        let isLong = delegateType === MixContractDelegateType.OPEN_LONG;
        let isShort = delegateType === MixContractDelegateType.OPEN_SHORT;
        if (isLong) {
          tokenSymbolSideFixedLongUsdtValue.add(unionKey, orderValue);
        }
        if (isShort) {
          tokenSymbolSideFixedShortUsdtValue.add(unionKey, orderValue);
        }
        // let positionValue = Zero
        // let isLong = delegateType === MixContractDelegateType.OPEN_LONG
        // let isShort = delegateType === MixContractDelegateType.OPEN_SHORT
        // // 尝试找到对应的仓位
        // const positions = positionList.filter(p => {
        //     if (p.symbolId === symbolId && p.tokenId === tokenId) {
        //         return (
        //             (isLong && p.holdSide === MixContractHoldSide.LONG_POSITIONS) ||
        //             (isShort && p.holdSide === MixContractHoldSide.SHORT_POSITIONS)
        //         )
        //     } else {
        //         return false
        //     }
        // })
        // //单方向的  委托是多 这里就是多 委托是空这里就是空
        // positionValue = position.dPricedValue

        // const feeRate = contractConfig.feeRate

        // const contractLevelList = get(
        //     ContractPositionLevelList(contractConfig.symbolId)
        // )
        // // 查询仓位档位
        // const levelInfo = getLevel(
        //     contractLevelList,
        //     orderValue.add(positionValue).toNumber()
        // )
        // let mm = levelInfo.keepMarginRate
        // let finalValue = orderValue
        //     .div(tokenMarkPrice)
        //     .times(new Decimal(mm).add(feeRate))
        // tokenTotalFixedOrderMaintainValue.add(tokenId, finalValue)
      });
    tokenSymbolSideFixedLongUsdtValue.forEach((orderValue, uniKey) => {
      // 尝试找到对应的仓位
      const position = positionList.find((p) => {
        if (generateUniKey(p.tokenId, p.symbolId) === uniKey) {
          return p.holdSide === MixContractHoldSide.LONG_POSITIONS;
        } else {
          return false;
        }
      });
      const { tokenId, symbolId } = inverseGenerateUniKey(uniKey);
      const contractConfig = getContractConfig(contractList, symbolId);
      // 保证金币种的汇价
      const tokenMarkPrice = get(getTokenPriceByTokenId(tokenId)).indexPrice;
      let positionValue = Zero;
      if (position) {
        //单方向的  委托是多 这里就是多 委托是空这里就是空
        positionValue = position.dPricedValueForMM;
      }

      const feeRate = contractConfig.feeRate;

      const contractLevelList = get(
        ContractPositionLevelList(contractConfig.symbolId)
      );
      // 查询仓位档位
      const levelInfo = getLevel(
        contractLevelList,
        orderValue.add(positionValue).toNumber()
      );
      let mm = levelInfo.keepMarginRate;
      let finalValue = orderValue
        .div(tokenMarkPrice)
        .times(new Decimal(mm).add(feeRate));
      tokenTotalFixedOrderMaintainValue.add(tokenId, finalValue);
    });
    tokenSymbolSideFixedShortUsdtValue.forEach((orderValue, uniKey) => {
      // 尝试找到对应的仓位
      const position = positionList.find((p) => {
        if (generateUniKey(p.tokenId, p.symbolId) === uniKey) {
          return p.holdSide === MixContractHoldSide.SHORT_POSITIONS;
        } else {
          return false;
        }
      });
      const { tokenId, symbolId } = inverseGenerateUniKey(uniKey);
      const contractConfig = getContractConfig(contractList, symbolId);
      // 保证金币种的汇价
      const tokenMarkPrice = get(getTokenPriceByTokenId(tokenId)).indexPrice;
      let positionValue = Zero;
      if (position) {
        //单方向的  委托是多 这里就是多 委托是空这里就是空
        positionValue = position.dPricedValueForMM;
      }

      const feeRate = contractConfig.feeRate;

      const contractLevelList = get(
        ContractPositionLevelList(contractConfig.symbolId)
      );
      // 查询仓位档位
      const levelInfo = getLevel(
        contractLevelList,
        orderValue.add(positionValue).toNumber()
      );
      let mm = levelInfo.keepMarginRate;
      let finalValue = orderValue
        .div(tokenMarkPrice)
        .times(new Decimal(mm).add(feeRate));
      tokenTotalFixedOrderMaintainValue.add(tokenId, finalValue);
    });
    return tokenTotalFixedOrderMaintainValue;
  },
});

// /**
//  * 逐仓委托维持保证金额
//  */
// export const FixedOrderMaintainValueSelector = selector({
//     key: 'fixed-order-maintain-value-selector',
//     get: ({ get }) => {
//         const orders = get(MixContractFixedCurrentOrdersSelector)
//         const positionList = get(MixContractBaseFixedPositionListSelector)
//         const collector = get(FixedOrderMaintainValueCollector)

//         return collector(orders, positionList)
//     }
// })
/**
 * 止盈止损弹窗标题类型
 */

export const tpslTitleIndex = atom({
  key: "tpsl-title-index",
  default: 1,
});

export interface MixContractOrderShareInfo {
  backImgList: string[];
  qrcUrlList: string[];
  keyInfo: string; //用户信息
  valueInfo: string; //邮箱或手机号
  keyInvite: string; //邀请码-种类名称
  valueInvite: string; //邀请码
  content: string; //邀请内容
  link: string; //邀请链接
  community: Array<any>; //社区
}

/**
 * 合约订单分享信息
 */
export const MixContractOrderShareInfoState = atom<MixContractOrderShareInfo>({
  key: "mix-ct-order-share-info-state",
  default: null as unknown as MixContractOrderShareInfo,
});

/**
 * 合约-用户分享信息
 */
export const UserShareInfoByBizTypeSelector = selectorFamily({
  key: "user-share-info-state",
  get: (bizType: number) => async () => {
    try {
      const res = await getShareInfoApi({
        bizType: bizType,
      });
      if (isSuccess(res.code)) {
        const data = res?.data || {};
        return data;
      }
      return {};
    } catch (error) {}
  },
});
